import { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { useLocation } from "react-router";
import { COOKIE_CONSENT_EVENT, getSavedCookieConsent } from "./CookieBanner";

const GA_MEASUREMENT_ID = "G-2WCPLESD05";

function initializeGoogleAnalytics() {
  if (ReactGA.isInitialized) return;

  ReactGA.initialize(GA_MEASUREMENT_ID, {
    gtagOptions: {
      send_page_view: false,
    },
  });
}

function grantGoogleAnalyticsConsent() {
  ReactGA.gtag("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
}

function trackPageView(path: string) {
  grantGoogleAnalyticsConsent();
  initializeGoogleAnalytics();

  ReactGA.event("page_view", {
    send_to: GA_MEASUREMENT_ID,
    page_title: document.title,
    page_location: `${window.location.origin}${path}`,
  });
}

function trackSectionNavClick(anchor: HTMLAnchorElement) {
  grantGoogleAnalyticsConsent();
  initializeGoogleAnalytics();

  const url = new URL(anchor.href);

  ReactGA.event("section_nav_click", {
    send_to: GA_MEASUREMENT_ID,
    section_id: url.hash.replace(/^#/, ""),
    link_text: anchor.textContent?.trim() || undefined,
    link_url: url.href,
    page_location: window.location.href,
  });
}

export function GoogleAnalytics() {
  const location = useLocation();
  const [hasConsent, setHasConsent] = useState(false);

  useEffect(() => {
    setHasConsent(getSavedCookieConsent() === "accepted");

    const handleConsentChange = () => {
      setHasConsent(getSavedCookieConsent() === "accepted");
    };

    window.addEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_EVENT, handleConsentChange);
    };
  }, []);

  useEffect(() => {
    if (!hasConsent) return;

    trackPageView(`${location.pathname}${location.search}`);
  }, [hasConsent, location.pathname, location.search]);

  useEffect(() => {
    if (!hasConsent) return;

    const handleSectionNavClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const anchor = target.closest<HTMLAnchorElement>("a[href*='#']");
      if (!anchor) return;

      const url = new URL(anchor.href);
      if (!url.hash || url.origin !== window.location.origin) return;
      if (url.pathname !== window.location.pathname) return;
      if (url.search !== window.location.search) return;

      trackSectionNavClick(anchor);
    };

    document.addEventListener("click", handleSectionNavClick);
    return () => {
      document.removeEventListener("click", handleSectionNavClick);
    };
  }, [hasConsent]);

  return null;
}
